/**
 * NotePaymentsTab — the Notes tab on Payment Matching (T3-155).
 *
 * Cash for notes is matched against a note's Interest / Capital Payment rows, in two kinds:
 *   - Investor payments: cash paid to the investor (owed = the row's net interest due, or the
 *     nominal on the capital row);
 *   - Withholding tax:   the tax remitted to the revenue authority on an interest row.
 * Commission is house income and is never matched. Interest Accrual rows are never payable.
 *
 * ⚠ SELF-CONTAINED ON PURPOSE. This tab has its own feeds (GET /notes/payments/*) and its own
 * submit handler, rather than joining PaymentMatching's shared contract handler and its four
 * payment-kind registries. Note payments live in note_payments, not contract_payments (the
 * Broker Fees precedent), so routing them through the contract path would only add ways to
 * misclassify a row. PaymentMatching renders this when the Notes tab is active.
 *
 * The rules (coverage, partial + top-up, no overpayment, a reason for a short payment) are
 * enforced by sp_note_payment_match; this component never decides what is owed.
 */
import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert, Box, Button, Chip, Link, Paper, Tab, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Tabs, Typography,
} from '@mui/material';
import { apiGet, apiPost, apiUpload, extractApiData, extractApiError } from '../../utils/apiClient';
import { formatNumber, formatShortDate } from '../../utils/formatters';
import { usePermissions } from '../../contexts/PermissionContext';
import { useNotification } from '../../contexts/NotificationContext';
import { MODULES } from '../../contexts/PermissionConstants';
import useDocumentActions from '../../hooks/useDocumentActions';
import DocumentPreviewDialog from '../documents/DocumentPreviewDialog';
import ConfirmDialog from '../common/ConfirmDialog';
import LoadingSpinner from '../common/LoadingSpinner';
import TabPanel from '../common/TabPanel';
import MatchDialog from '../payments/MatchDialog';
import PaymentProofCell from '../payments/PaymentProofCell';
import logger from '../../utils/logger';

const money = (v) => formatNumber(v, 2);
const head = { fontWeight: 600, whiteSpace: 'nowrap' };

// Shape a queue row the way MatchDialog reads schedule rows (house triple + interest/capital).
const toDialogRow = (r) => ({
  id: r.note_schedule_id,
  note_schedule_id: r.note_schedule_id,
  note_id: r.note_id,
  investor_id: r.investor_id,
  payment_kind: r.payment_kind,
  narration: r.narration,
  payment_date: r.payment_date,
  scheduled_amount: r.scheduled_amount,
  matched_amount: r.matched_amount,
  payment_amount: r.outstanding_amount,
  interest_amount: r.payment_kind === 'wht' ? 0 : r.interest_amount,
  capital_amount: r.payment_kind === 'wht' ? 0 : r.capital_amount,
  currency_code: r.currency_code,
  code: r.code,
  investor_name: r.investor_name,
});

const statusChip = (r) => {
  if (r.days_overdue > 0) return <Chip label={`${r.days_overdue}d overdue`} size="small" color="error" variant="outlined" />;
  if (parseFloat(r.matched_amount) > 0) return <Chip label="Part paid" size="small" color="warning" variant="outlined" />;
  return <Chip label="Pending" size="small" variant="outlined" />;
};

const NotePaymentsTab = () => {
  const navigate = useNavigate();
  const notify = useNotification();
  const { hasAction } = usePermissions();
  const canMatch = hasAction(MODULES.NOTES, 'update');
  const docActions = useDocumentActions();

  const [sub, setSub] = useState(0);
  const [investorRows, setInvestorRows] = useState([]);
  const [whtRows, setWhtRows] = useState([]);
  const [matched, setMatched] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dialogRow, setDialogRow] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [confirm, setConfirm] = useState(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      const [inv, wht, done] = await Promise.all([
        apiGet('/notes/payments/pending', { params: { kind: 'investor' } }),
        apiGet('/notes/payments/pending', { params: { kind: 'wht' } }),
        apiGet('/notes/payments/matched'),
      ]);
      setInvestorRows(extractApiData(inv));
      setWhtRows(extractApiData(wht));
      setMatched(extractApiData(done));
    } catch (err) {
      setError(extractApiError(err, 'Could not load note payments'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Upload every staged proof first (fail loud: nothing is matched if an upload fails), then
  // match. Proofs are filed against the investor — notes have no contract id.
  const handleSubmit = async ({ payment, variance }) => {
    if (!dialogRow) return;
    setSubmitting(true);
    try {
      const proofIds = [];
      for (const file of payment.proof_files || []) {
        const form = new FormData();
        form.append('file', file);
        form.append('document_type', dialogRow.payment_kind === 'wht' ? 'proof_of_wht_payment' : 'proof_of_payment');
        form.append('investor_id', dialogRow.investor_id);
        form.append('notes', `Proof of payment for note ${dialogRow.code} - ${payment.payment_reference || 'No reference'}`);
        let up;
        try {
          up = await apiUpload('/admin/documents/upload', form);
        } catch (uploadErr) {
          notify.error(`Could not upload "${file.name}": ${extractApiError(uploadErr, 'upload failed')}. The payment was NOT matched.`);
          return;
        }
        const docId = up?.data?.id;
        if (!docId) {
          notify.error(`Upload of "${file.name}" returned no document id. The payment was NOT matched.`);
          return;
        }
        proofIds.push(docId);
      }
      await apiPost('/notes/payments/match', {
        note_schedule_id: dialogRow.note_schedule_id,
        kind: dialogRow.payment_kind,
        payment_amount: payment.payment_amount,
        payment_date: payment.payment_date,
        payment_reference: payment.payment_reference,
        payment_method: payment.payment_method,
        variance_reason: variance?.reason || null,
        notes: payment.notes,
        proof_document_ids: proofIds,
      });
      notify.success('Payment matched');
      setDialogRow(null);
      await load();
    } catch (err) {
      logger.error('Error matching note payment', { message: err.message, status: err.response?.status });
      notify.error(`Failed to match payment: ${extractApiError(err, err.message)}`);
    } finally {
      setSubmitting(false);
    }
  };

  const askUnmatch = (p) => setConfirm({
    title: p.payment_kind === 'investor' ? 'Unmatch Note Payment' : 'Unmatch Withholding Tax',
    message: p.payment_kind === 'investor'
      ? 'Unmatch this payment? It returns to Pending and the row is owed again. Any withholding tax '
        + 'remitted on the same row is ALSO returned to Pending.'
      : 'Unmatch this withholding tax remittance? It returns to Pending and the tax is owed again.',
    onConfirm: async () => {
      try {
        const res = await apiPost(`/notes/payments/${p.id}/unmatch`);
        const cascaded = extractApiData(res, {})?.cascaded_wht_payment_ids || [];
        notify.success(cascaded.length ? 'Payment unmatched. Its withholding tax was also returned to Pending.' : 'Payment unmatched');
        await load();
      } catch (err) {
        notify.error(`Failed to unmatch: ${extractApiError(err, err.message)}`);
      } finally {
        setConfirm(null);
      }
    },
  });

  // T3-158: note_reference is the user reference (FIN15052026); the system reference shows on hover.
  const noteLink = (r) => (
    <Link component="button" underline="hover" onClick={() => navigate(`/notes/${r.note_id}`)} sx={{ fontWeight: 600 }}
      title={r.note_system_reference || undefined}>
      {r.note_reference}
    </Link>
  );

  const pendingTable = (rows, kind) => (
    <TableContainer>
      <Table size="small">
        <TableHead>
          <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
            <TableCell sx={head}>Note</TableCell>
            <TableCell sx={head}>Investor</TableCell>
            <TableCell sx={head}>Type</TableCell>
            <TableCell sx={head}>Due Date</TableCell>
            <TableCell sx={head} align="right">{kind === 'wht' ? 'WHT Due' : 'Amount Due'}</TableCell>
            <TableCell sx={head} align="right">Matched</TableCell>
            <TableCell sx={head} align="right">Outstanding</TableCell>
            <TableCell sx={head}>Status</TableCell>
            {canMatch && <TableCell sx={head} align="center">Actions</TableCell>}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.length === 0 ? (
            <TableRow>
              <TableCell colSpan={canMatch ? 9 : 8} align="center">
                <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                  {kind === 'wht' ? 'No withholding tax outstanding on notes' : 'No note payments outstanding'}
                </Typography>
              </TableCell>
            </TableRow>
          ) : rows.map((r) => (
            <TableRow key={`${r.note_schedule_id}-${kind}`} hover>
              <TableCell>{noteLink(r)}</TableCell>
              <TableCell>
                <Typography variant="body2">{r.investor_name}</Typography>
                <Typography variant="caption" color="text.secondary">{r.code}</Typography>
              </TableCell>
              <TableCell>
                <Chip size="small" variant="outlined" label={kind === 'wht' ? 'Withholding Tax' : r.narration}
                  color={kind === 'wht' ? 'secondary' : (r.row_kind === 'capital' ? 'primary' : 'default')} />
              </TableCell>
              <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatShortDate(r.payment_date)}</TableCell>
              <TableCell align="right">{r.currency_code} {money(r.scheduled_amount)}</TableCell>
              <TableCell align="right"><Typography variant="body2" color="text.secondary">{money(r.matched_amount)}</Typography></TableCell>
              <TableCell align="right"><Typography variant="body2" fontWeight="bold">{money(r.outstanding_amount)}</Typography></TableCell>
              <TableCell>{statusChip(r)}</TableCell>
              {canMatch && (
                <TableCell align="center">
                  <Button variant="outlined" size="small" onClick={() => setDialogRow(toDialogRow(r))}>Match</Button>
                </TableCell>
              )}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );

  if (loading) return <LoadingSpinner />;

  return (
    <Box>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      <Paper sx={{ mb: 0 }}>
        <Tabs value={sub} onChange={(_, v) => setSub(v)}>
          <Tab label={`Investor Payments (${investorRows.length})`} />
          <Tab label={`Withholding Tax (${whtRows.length})`} />
          <Tab label={`Matched (${matched.length})`} />
        </Tabs>
      </Paper>

      <TabPanel value={sub} index={0}><Paper>{pendingTable(investorRows, 'investor')}</Paper></TabPanel>
      <TabPanel value={sub} index={1}><Paper>{pendingTable(whtRows, 'wht')}</Paper></TabPanel>
      <TabPanel value={sub} index={2}>
        <Paper>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                  <TableCell sx={head}>Note</TableCell>
                  <TableCell sx={head}>Investor</TableCell>
                  <TableCell sx={head}>Paid For</TableCell>
                  <TableCell sx={head}>Due Date</TableCell>
                  <TableCell sx={head}>Paid On</TableCell>
                  <TableCell sx={head} align="right">Amount</TableCell>
                  <TableCell sx={head}>Reference</TableCell>
                  <TableCell sx={head}>Proof</TableCell>
                  <TableCell sx={head}>Matched By</TableCell>
                  {canMatch && <TableCell sx={head} align="center">Actions</TableCell>}
                </TableRow>
              </TableHead>
              <TableBody>
                {matched.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={canMatch ? 10 : 9} align="center">
                      <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>No matched note payments</Typography>
                    </TableCell>
                  </TableRow>
                ) : matched.map((p) => (
                  <TableRow key={p.id} hover>
                    <TableCell>{noteLink(p)}</TableCell>
                    <TableCell>
                      <Typography variant="body2">{p.investor_name}</Typography>
                      <Typography variant="caption" color="text.secondary">{p.code}</Typography>
                    </TableCell>
                    <TableCell>
                      {p.payment_kind === 'wht'
                        ? 'Withholding Tax'
                        : (p.row_kind === 'capital' ? 'Capital Payment' : 'Interest Payment')}
                      {p.variance_reason && (
                        <Typography variant="caption" color="text.secondary" display="block">{p.variance_reason}</Typography>
                      )}
                    </TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatShortDate(p.scheduled_date)}</TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatShortDate(p.payment_date)}</TableCell>
                    <TableCell align="right">{p.currency_code} {money(p.payment_amount)}</TableCell>
                    <TableCell>{p.payment_reference || '—'}</TableCell>
                    <TableCell><PaymentProofCell payment={p} docActions={docActions} /></TableCell>
                    <TableCell>{p.processed_by || '—'}</TableCell>
                    {canMatch && (
                      <TableCell align="center">
                        <Button size="small" color="warning" onClick={() => askUnmatch(p)}>Unmatch</Button>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      </TabPanel>

      {dialogRow && (
        <MatchDialog
          open
          onClose={() => setDialogRow(null)}
          onSubmit={handleSubmit}
          submitting={submitting}
          matchType={dialogRow.payment_kind === 'wht' ? 'note_wht' : 'note'}
          scheduleRow={dialogRow}
          currencyCode={dialogRow.currency_code}
          contractReference={dialogRow.code}
          investorName={dialogRow.investor_name}
        />
      )}
      <ConfirmDialog
        open={!!confirm}
        title={confirm?.title}
        message={confirm?.message}
        confirmText="Unmatch"
        confirmColor="warning"
        onConfirm={() => confirm?.onConfirm()}
        onCancel={() => setConfirm(null)}
      />
      <DocumentPreviewDialog {...docActions} />
    </Box>
  );
};

export default NotePaymentsTab;
